import * as React from "react"

import { EmptyState, Skeleton } from "@/components/ui/alert"
import { useCtrlWheelZoom, useScrollPan, useScrollZoomAnchor } from "@/lib/pan-zoom"
import { cn } from "@/lib/utils"

import type { EvalFinding, EvalMark } from "../eval-runs-data"

import { PageOverlays } from "./page-overlays"
import { anchorOf, type Selection } from "./selection"
import { PdfPage } from "./pdf-page"
import type { PdfDocumentKind, PdfState } from "./use-pdf-document"
import { useInnerWidth, ZOOM_MAX, ZOOM_MIN } from "./use-zoom-pan"
import { ViewerToolbar, type PageMode } from "./viewer-toolbar"

// The label PDF with the expert's marks and our findings drawn over it. One page at a time or
// every page scrolled, zoomed and panned as in any PDF viewer; pages render as they near the view,
// so a 46-page booklet costs what the pages on screen cost.

const MODE_KEY = "pyxis:eval-runs:page-mode"
const ZOOM_ANIMATION_MS = 180

function readMode(): PageMode {
  try {
    return localStorage.getItem(MODE_KEY) === "continuous" ? "continuous" : "single"
  } catch {
    return "single"
  }
}

const COPY: Record<
  PdfDocumentKind,
  { region: string; missing: string; failed: string; hint: string }
> = {
  label: {
    region: "Label PDF",
    missing: "This label was published without its PDF.",
    failed: "The label PDF could not be displayed",
    hint: "Ctrl/⌘ + scroll to zoom · drag to move around a zoomed page · click a box to remark on it",
  },
  master: {
    region: "Master PDF",
    missing: "The master PDF was not uploaded with this run.",
    failed: "The master PDF could not be displayed",
    hint: "Ctrl/⌘ + scroll to zoom · drag to move around a zoomed page",
  },
}

export interface PdfViewerProps {
  /** Which document this is: the label carries the boxes, the master is shown bare. */
  document?: PdfDocumentKind
  hasPdf: boolean
  /** The label's PDF, loaded by the page: it also decides where a remark card can open. */
  pdf: PdfState
  page: number
  setPage: (page: number) => void
  marks: readonly EvalMark[]
  findings: readonly EvalFinding[]
  selection: Selection
  onSelect: (s: Selection) => void
  remarkCounts: ReadonlyMap<string, number>
  card: React.ReactNode
  /**
   * A page to bring into view from outside the viewer (the master following the label). `key`
   * says when it is a new request: the same page asked for again after the reader moved away.
   */
  goTo?: { key: string; page: number } | null
  /** A quiet line beside the pager. */
  note?: React.ReactNode
}

export function PdfViewer(props: PdfViewerProps) {
  const { page, setPage, selection } = props
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const width = useInnerWidth(viewportRef)
  const { pdf } = props
  const copy = COPY[props.document ?? "label"]
  const [zoom, setZoom] = React.useState(1)
  const [mode, setMode] = React.useState<PageMode>(readMode)
  // The viewport's inner width less its padding; the scrollbar gutter is reserved, so "fit to
  // width" fits whether or not the document is tall enough to scroll.
  const target = width > 0 ? Math.floor((width - 16) * zoom) : 0
  const anchorZoom = useScrollZoomAnchor(viewportRef, target)
  // The zoom as last asked for, ahead of the render: wheel notches arrive faster than renders,
  // and each must multiply the one before it rather than the zoom on screen.
  const zoomRef = React.useRef(zoom)
  const zoomTo = (next: number, at?: { x: number; y: number }) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
    // Anchored only when the zoom moves: at a limit the layout never changes, and an anchor
    // left waiting would be applied to whatever change came next.
    if (clamped !== zoomRef.current) anchorZoom(at)
    zoomRef.current = clamped
    setZoom(clamped)
  }
  // Ctrl/⌘ + wheel (or a trackpad pinch) zooms around the pointer. Bound by the hook rather than
  // `onWheel`, which React makes passive, so the browser would zoom the whole page as well.
  useCtrlWheelZoom(viewportRef, (factor, x, y) => {
    cancelAnimationFrame(zoomAnimation.current)
    const rect = viewportRef.current?.getBoundingClientRect()
    zoomTo(zoomRef.current * factor, rect ? { x: rect.left + x, y: rect.top + y } : undefined)
  })
  // The buttons ease to the new zoom over a few frames instead of jumping; the pages resize as
  // they go and are re-rendered sharp once it settles.
  const zoomAnimation = React.useRef(0)
  const animateZoomTo = (next: number) => {
    cancelAnimationFrame(zoomAnimation.current)
    const from = zoomRef.current
    const to = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
    const started = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - started) / ZOOM_ANIMATION_MS)
      const eased = 1 - (1 - t) ** 3
      zoomTo(from * (to / from) ** eased)
      if (t < 1) zoomAnimation.current = requestAnimationFrame(step)
    }
    zoomAnimation.current = requestAnimationFrame(step)
  }
  React.useEffect(
    () => () => {
      cancelAnimationFrame(zoomAnimation.current)
    },
    [],
  )
  const pan = useScrollPan(viewportRef)
  const pageRefs = React.useRef(new Map<number, HTMLDivElement>())
  const pageCount = pdf.status === "ready" ? pdf.doc.numPages : 1
  // Set while the view scrolls itself to a page, so the scroll spy does not drag the page
  // number to wherever that scroll passes through.
  const jumping = React.useRef(false)

  const chooseMode = (next: PageMode) => {
    setMode(next)
    try {
      localStorage.setItem(MODE_KEY, next)
    } catch {
      // Blocked storage: the choice just does not persist.
    }
  }

  // The pending jump's cleanup: a newer jump cancels it, so an older jump's timer cannot hand
  // the page number back to the spy while the newer glide is still running.
  const cancelJump = React.useRef<(() => void) | null>(null)
  React.useEffect(
    () => () => {
      cancelJump.current?.()
    },
    [],
  )

  const scrollToPage = React.useCallback(
    (index: number, focus?: { x: number; y: number }) => {
      const view = viewportRef.current
      if (!view) return
      cancelJump.current?.()
      const el = mode === "continuous" ? pageRefs.current.get(index) : null
      const top = el ? el.offsetTop : 0
      jumping.current = true
      view.scrollTo({
        left: focus ? focus.x - view.clientWidth / 2 : view.scrollLeft,
        top: focus ? top + focus.y - view.clientHeight / 4 : Math.max(0, top - 8),
        behavior: "smooth",
      })
      // The spy resumes when the glide ends; the timer covers a browser without `scrollend`
      // and a scroll that had nowhere to go.
      const cancel = () => {
        window.clearTimeout(timer)
        view.removeEventListener("scrollend", done)
        if (cancelJump.current === cancel) cancelJump.current = null
      }
      const done = () => {
        jumping.current = false
        cancel()
      }
      view.addEventListener("scrollend", done)
      const timer = window.setTimeout(done, 1000)
      cancelJump.current = cancel
    },
    [mode],
  )

  const changePage = (index: number) => {
    setPage(index)
    scrollToPage(index)
  }

  // A selected box: bring its page into view, and the box near the top of it.
  const anchor = anchorOf(selection, props.marks, props.findings)
  const anchorKey = anchor ? `${String(anchor.page)}:${anchor.box.join(",")}` : ""
  const scale = pdf.status === "ready" && target > 0 ? target / pdf.pointWidth : 0
  // Read at the moment of the jump: a zoom changes the scale, and must not jump back to the box.
  const scaleRef = React.useRef(scale)
  scaleRef.current = scale
  const ready = scale > 0
  React.useEffect(() => {
    if (!anchor || !ready) return
    const id = window.setTimeout(() => {
      const s = scaleRef.current
      scrollToPage(anchor.page, { x: anchor.box[0] * s, y: anchor.box[1] * s })
    }, 50)
    return () => {
      window.clearTimeout(id)
    }
    // A new selection, the document becoming ready or a change of view decide this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, ready, mode])

  // A page asked for from outside: shown once the document can be laid out.
  const goTo = props.goTo ?? null
  const goKey = goTo ? `${goTo.key}:${String(goTo.page)}` : ""
  React.useEffect(() => {
    if (!goTo || !ready) return
    const target = Math.min(goTo.page, pageCount - 1)
    setPage(target)
    scrollToPage(target)
    // A new request or the document becoming ready decide this, not each render's object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goKey, ready])

  // In the scrolled view, the page number follows the page filling the view.
  React.useEffect(() => {
    const view = viewportRef.current
    if (mode !== "continuous" || !view) return
    const onScroll = () => {
      if (jumping.current) return
      const line = view.scrollTop + view.clientHeight / 3
      let current = 0
      for (const [index, el] of pageRefs.current) {
        if (el.offsetTop <= line && index > current) current = index
      }
      if (current !== page) setPage(current)
    }
    view.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      view.removeEventListener("scroll", onScroll)
    }
  }, [mode, page, setPage])

  const pages =
    mode === "continuous"
      ? Array.from({ length: pageCount }, (_, i) => i)
      : [Math.min(page, pageCount - 1)]

  return (
    <section className="min-w-0" aria-label={copy.region}>
      <ViewerToolbar
        page={page}
        pageCount={pageCount}
        onPage={changePage}
        mode={mode}
        onMode={chooseMode}
        zoom={zoom}
        onZoom={(z) => {
          animateZoomTo(z)
        }}
        legend={(props.document ?? "label") === "label"}
        note={props.note}
      />
      <div
        ref={viewportRef}
        className={cn(
          "h-[78vh] w-full overflow-auto rounded-md border border-border bg-canvas p-2",
          pan.canPan && "select-none",
          pan.canPan && (pan.dragging ? "cursor-grabbing" : "cursor-grab"),
        )}
        style={{ scrollbarGutter: "stable" }}
        {...pan.handlers}
      >
        <div className="flex w-max min-w-full flex-col items-center gap-3">
          {!props.hasPdf && <EmptyState title="No PDF" body={copy.missing} />}
          {props.hasPdf && pdf.status === "error" && (
            <EmptyState reason="error" title={copy.failed} body={pdf.message} />
          )}
          {props.hasPdf && pdf.status === "loading" && (
            <Skeleton className="aspect-[3/4] w-full" aria-busy />
          )}
          {props.hasPdf &&
            pdf.status === "ready" &&
            target > 0 &&
            pages.map((index) => (
              <div
                key={index}
                ref={(el) => {
                  if (el) pageRefs.current.set(index, el)
                  else pageRefs.current.delete(index)
                }}
              >
                <PdfPage
                  doc={pdf.doc}
                  index={index}
                  width={target}
                  aspect={pdf.aspect}
                  lazy={mode === "continuous"}
                  root={viewportRef}
                  overlays={(geometry) => (
                    <PageOverlays
                      page={index}
                      geometry={geometry}
                      marks={props.marks}
                      findings={props.findings}
                      selection={selection}
                      onSelect={props.onSelect}
                      remarkCounts={props.remarkCounts}
                      card={props.card}
                    />
                  )}
                />
              </div>
            ))}
        </div>
      </div>
      <p className="mt-1 text-caption text-text-3">{copy.hint}</p>
    </section>
  )
}
